import React,{useEffect,useState} from 'react';
import {AnimatePresence,motion} from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {leaseDynamicSlot} from '../hooks/useSlotLease';

const {FiActivity,FiArrowRight,FiCheck,FiShield,FiSmile,FiTarget}=FiIcons;

const LEGUP_LOGO='https://media-manager-c.questera.ai/greta-media/64b6f00450a2ccd8585f32418c01e01155b9a46657aed859be5d908803e7074989df043f4ede815d2dc0fa021038408b/images/aW1hZ2UvcG5n/0f94f030df42795442b8273a74c4eeec.png';
const LEGUP_LOGO_FALLBACK='https://i.ibb.co/0jdbffs7/leguprecovery.png';

const getChartOptions=(isMobile)=> ({
  grid: {top: 28,right: isMobile ? 12 : 28,bottom: isMobile ? 72 : 52,left: isMobile ? 38 : 42},
  tooltip: {trigger: 'axis',valueFormatter: (v)=> `${v}%`},
  legend: {
    bottom: 0,left: 'center',icon: 'circle',
    itemGap: isMobile ? 8 : 20,
    textStyle: {color: '#475569',fontSize: isMobile ? 10 : 13},
    formatter: (name)=> isMobile ? (name.includes('Without') ? 'Without GLP-1' : 'With GLP-1') : name
  },
  xAxis: {
    type: 'category',
    data: isMobile ? ['Start','Mo.1','Mo.2','Mo.3','Mo.4','Mo.5','Mo.6'] : ['Start','Month 1','Month 2','Month 3','Month 4','Month 5','Month 6'],
    boundaryGap: false,
    axisLine: {lineStyle: {color: '#cbd5e1'}},axisTick: {show: false},
    axisLabel: {color: '#64748b',fontSize: isMobile ? 9 : 11,interval: 0,rotate: isMobile ? 30 : 0}
  },
  yAxis: {
    type: 'value',min: 85,max: 102,interval: 5,
    name: isMobile ? '' : 'Starting body weight',
    nameTextStyle: {color: '#64748b',fontSize: 11,padding: [0,0,0,8]},
    axisLine: {show: false},axisTick: {show: false},
    axisLabel: {color: '#64748b',fontSize: isMobile ? 9 : 11,formatter: '{value}%'},
    splitLine: {lineStyle: {type: 'dashed',color: '#cbd5e1'}}
  },
  series: [
    {name: 'Without GLP-1 medications',type: 'line',data: [100,99.7,99.4,99.1,98.8,98.6,98.4],smooth: true,symbol: 'circle',symbolSize: isMobile ? 5 : 7,itemStyle: {color: '#6366f1'},lineStyle: {width: isMobile ? 2 : 3}},
    {name: 'With GLP-1 medications',type: 'line',data: [100,98.4,96.6,94.7,92.8,90.9,89.1],smooth: true,symbol: 'circle',symbolSize: isMobile ? 6 : 9,itemStyle: {color: '#d946ef',borderColor: '#fff',borderWidth: 2},lineStyle: {width: isMobile ? 2 : 3},areaStyle: {color: {type: 'linear',x: 0,y: 0,x2: 0,y2: 1,colorStops: [{offset: 0,color: 'rgba(217,70,239,0.2)'},{offset: 1,color: 'rgba(217,70,239,0)'}]}}}
  ]
});

const goalOptions=[
  {id: 'health',icon: FiActivity,text: 'Improve my general health'},
  {id: 'confidence',icon: FiSmile,text: 'Feel more confident'},
  {id: 'event',icon: FiTarget,text: 'Lose weight for a special event'}
];

function ProgressBar({step}) {
  return (
    <div className="mb-8">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Your Progress</div>
      <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
        <motion.div className="h-full bg-blue-400 rounded-full"
          initial={{width: `${((step - 1) / 4) * 100}%`}}
          animate={{width: `${(step / 4) * 100}%`}}
          transition={{duration: 0.5,ease: 'easeInOut'}} />
      </div>
    </div>
  );
}

function LegupLogo() {
  const [src,setSrc]=useState(LEGUP_LOGO);
  return (
    <div className="mt-6 flex items-center justify-center opacity-40">
      <img src={src} alt="Leg Up Recovery" className="h-7 w-auto object-contain"
        onError={()=> setSrc(LEGUP_LOGO_FALLBACK)} />
    </div>
  );
}

function ChartStep({onNext}) {
  const [isMobile,setIsMobile]=useState(window.innerWidth < 640);
  useEffect(()=> {
    const handler=()=> setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize',handler);
    return ()=> window.removeEventListener('resize',handler);
  },[]);
  return (
    <motion.div key="results" initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}}
      className="bg-white rounded-[2rem] p-6 md:p-12 shadow-xl shadow-slate-200/50 border border-slate-100">
      <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-3 font-serif tracking-tight">GLP-1 meds work!</h2>
      <p className="text-base text-slate-600 mb-6">Semaglutide users lost ~10.9% body weight in 6 months.*</p>
      <div className="w-full mb-2" style={{height: isMobile ? '280px' : '350px'}}>
        <ReactECharts option={getChartOptions(isMobile)} style={{height: '100%',width: '100%'}} />
      </div>
      <p className="text-xs text-slate-400 text-center mb-6">*Illustrative. Individual results vary.</p>
      <button onClick={onNext} className="w-full bg-[#54b4f9] hover:bg-[#43a3e8] text-white py-5 rounded-2xl text-xl font-bold transition-all shadow-lg">Continue</button>
      <LegupLogo />
    </motion.div>
  );
}

export default function Quiz() {
  const [step,setStep]=useState(1);
  const [isReservingSlot,setIsReservingSlot]=useState(false);
  const [leasedSlot,setLeasedSlot]=useState(null);
  const [slotError,setSlotError]=useState('');
  const [acknowledged,setAcknowledged]=useState(false);

  useEffect(()=> {window.scrollTo(0,0);},[step]);

  const reserveSlot=async ()=> {
    if (isReservingSlot) return;
    setIsReservingSlot(true);
    setSlotError('');
    try {
      const result=await leaseDynamicSlot(localStorage.getItem('glitchy_mock_backend')==='true');
      if (result.error) {setSlotError(result.error);return;}
      setLeasedSlot(result.slot);
      setAcknowledged(false);
      setStep(5);
    } finally {setIsReservingSlot(false);}
  };

  const startIntake=()=> {
    if (!acknowledged) return;
    const url=new URL('https://medical.leguprecovery.com/start-online-visit/sema-personalized-intake-vialsrx');
    url.searchParams.set('partner_id','GJQPQTLQ');
    if (leasedSlot !==null && leasedSlot !==undefined) url.searchParams.set('sub',String(leasedSlot));
    window.location.href=url.toString();
  };

  const deliveryDate=()=> {
    const d=new Date();
    d.setDate(d.getDate() + 3);
    return d.toLocaleDateString('en-US',{weekday: 'long',month: 'long',day: 'numeric'});
  };

  return (
    <div className="min-h-screen bg-[#fdfbf9] pt-24 pb-20 flex flex-col items-center justify-center px-4 sm:px-6">
      <div className="w-full max-w-3xl">
        {step < 5 && <ProgressBar step={step} />}
        <AnimatePresence mode="wait">

          {step===1 && (
            <motion.div key="sex" initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}}
              className="bg-white rounded-[2rem] p-8 md:p-12 shadow-xl shadow-slate-200/50 border border-slate-100">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-700 mb-6">
                <SafeIcon icon={FiShield} /> Weight Loss
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 font-serif tracking-tight">What is your biological sex?</h2>
              <p className="text-lg text-slate-600 mb-10">This helps your doctor understand how your body may respond to treatment.</p>
              <div className="grid sm:grid-cols-2 gap-6">
                {['Male','Female'].map((option)=> (
                  <button key={option} onClick={()=> setStep(2)}
                    className="bg-[#54b4f9] hover:bg-[#43a3e8] text-white rounded-2xl p-10 text-2xl font-semibold transition-all duration-300 hover:-translate-y-1 shadow-lg">
                    {option}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step===2 && <ChartStep key="results" onNext={()=> setStep(3)} />}

          {step===3 && (
            <motion.div key="goal" initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}}
              className="bg-white rounded-[2rem] p-8 md:p-12 shadow-xl shadow-slate-200/50 border border-slate-100">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 font-serif tracking-tight">What is your primary goal?</h2>
              <p className="text-lg text-slate-600 mb-10">Select the option that best describes your motivation.</p>
              <div className="space-y-4">
                {goalOptions.map((option)=> (
                  <button key={option.id} onClick={()=> setStep(4)}
                    className="w-full bg-white border-2 border-slate-100 hover:border-[#54b4f9] hover:bg-blue-50 text-slate-700 py-5 px-6 rounded-2xl text-lg font-semibold transition-all flex items-center gap-4 text-left group">
                    <div className="w-10 h-10 rounded-full bg-slate-50 group-hover:bg-blue-100 flex items-center justify-center text-slate-400 group-hover:text-[#54b4f9]">
                      <SafeIcon icon={option.icon} className="w-5 h-5" />
                    </div>
                    {option.text}
                  </button>
                ))}
              </div>
              <LegupLogo />
            </motion.div>
          )}

          {step===4 && (
            <motion.div key="eligibility" initial={{opacity: 0,scale: 0.95}} animate={{opacity: 1,scale: 1}}
              className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <div className="p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-8 font-serif tracking-tight leading-tight text-center">
                  We specialize in weight loss with GLP-1 medications.
                </h2>
                <div className="space-y-4 mb-10">
                  {['All telemed medical practitioners are licensed and insured.','Upon prescription approval, your medication will ship within 1 business day.','Medications are shipped via temperature-controlled 2 day delivery.','All shipments are insured and tracked to your door.'].map((item)=> (
                    <div key={item} className="flex items-start gap-4">
                      <div className="mt-1 bg-green-100 text-green-600 p-1 rounded-full shrink-0"><SafeIcon icon={FiCheck} className="w-4 h-4" /></div>
                      <p className="text-slate-700 text-lg">{item}</p>
                    </div>
                  ))}
                </div>
                <button onClick={reserveSlot} disabled={isReservingSlot}
                  className="w-full bg-[#54b4f9] hover:bg-[#43a3e8] disabled:opacity-70 text-white py-5 rounded-2xl text-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2">
                  {isReservingSlot ? 'Checking Eligibility...' : 'Check Eligibility'}
                  {!isReservingSlot && <SafeIcon icon={FiArrowRight} />}
                </button>
                {slotError && <p className="text-center text-sm font-medium text-red-600 mt-5">{slotError}</p>}
                <div className="text-center my-8">
                  <p className="text-slate-500 font-medium mb-2 uppercase tracking-wider text-sm">Your expected delivery date:</p>
                  <p className="text-2xl font-extrabold text-slate-900">{deliveryDate()}</p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 text-center">
                  <p className="text-blue-900 font-bold text-lg">2 consultations available today.</p>
                  <p className="text-blue-600 text-sm mt-1">Included with your purchase.</p>
                </div>
                <LegupLogo />
              </div>
            </motion.div>
          )}

          {step===5 && (
            <motion.div key="complete" initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{duration: 0.6}}
              className="bg-white rounded-[2rem] shadow-2xl border-t-8 border-[#023B6D] overflow-hidden max-w-2xl mx-auto">
              <div className="p-8 md:p-12 text-center">
                <motion.div
                  initial={{scale: 0,rotate: -30}} animate={{scale: [0,1.12,1],rotate: 0}}
                  transition={{type: 'spring',stiffness: 200,damping: 14}}
                  className="mx-auto mb-6 h-20 w-20 bg-green-50 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-500" />
                </motion.div>
                <h2 className="text-3xl font-extrabold text-[#023B6D] mb-3">You're eligible!</h2>
                <p className="text-slate-600 mb-8">Your personalized weight-loss plan is ready — a licensed physician will hand-pick the best medication to match your body and goals.</p>
                <motion.label
                  animate={acknowledged ? {scale: 1} : {scale: [1,1.018,1],boxShadow: ['0 0 0 0 rgba(84,180,249,0)','0 0 0 10px rgba(84,180,249,0.16)','0 0 0 0 rgba(84,180,249,0)']}}
                  transition={{duration: 1.5,repeat: acknowledged ? 0 : Infinity}}
                  className={`mb-6 flex items-start gap-4 rounded-xl border-2 p-5 text-left cursor-pointer ${acknowledged ? 'bg-green-50 border-green-400' : 'bg-white border-[#54b4f9]'}`}>
                  <input type="checkbox" checked={acknowledged} onChange={(e)=> setAcknowledged(e.target.checked)} className="sr-only" />
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 ${acknowledged ? 'bg-green-500 border-green-500' : 'border-[#54b4f9]'}`}>
                    {acknowledged && <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />}
                  </span>
                  <span className="text-sm leading-relaxed text-[#023B6D]">
                    I understand my doctor will review my intake and choose the right product for me.
                  </span>
                </motion.label>
                {!acknowledged && <p className="text-sm font-bold text-[#54b4f9] -mt-3 mb-5">Please check the box above to continue.</p>}
                <button onClick={startIntake} disabled={!acknowledged}
                  className="w-full bg-[#023B6D] hover:bg-[#012A4F] disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white py-4 rounded-xl text-xl font-bold transition-all flex items-center justify-center">
                  Enter Your Email to Get Started
                </button>
                <LegupLogo />
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}